"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import type { RegexTesterStrings as Strings } from "@/components/tools/regex-tester";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type {
  ExplainKind,
  ExplainToken,
} from "@/lib/tool-kit/developer/regex/explain";
import type { RegexMatch } from "@/lib/tool-kit/developer/regex/run";

/*
 * The regex tester's explanation list and match table. Every word comes from the
 * widget's strings.
 */

type Format = (value: number) => string;
type PlainKind = Exclude<
  ExplainKind,
  "special" | "exactly" | "atLeast" | "between"
>;
type SpecialName = keyof Strings["specialNames"];

function describeQuantifier(
  token: ExplainToken,
  strings: Strings,
  locale: string,
  fmt: Format
): string | null {
  const min = Number(token.values.min ?? 0);
  if (token.kind === "exactly") {
    return tPlural(strings.explainExactly, locale, min, { count: fmt(min) });
  }
  if (token.kind === "atLeast") {
    return tPlural(strings.explainAtLeast, locale, min, { count: fmt(min) });
  }
  if (token.kind === "between") {
    const max = Number(token.values.max ?? 0);
    return tPlural(strings.explainBetween, locale, max, {
      count: fmt(max),
      min: fmt(min),
    });
  }
  return null;
}

export function describeToken(
  token: ExplainToken,
  strings: Strings,
  locale: string,
  fmt: Format
): string {
  let text: string;
  if (token.kind === "special") {
    const names: Record<string, string> = strings.specialNames;
    text = names[String(token.values.name) as SpecialName] ?? token.source;
  } else {
    text =
      describeQuantifier(token, strings, locale, fmt) ??
      t(strings.explain[token.kind as PlainKind], token.values);
  }
  return token.lazy ? t(strings.lazy, { rule: text.trim() }) : text.trim();
}

export function ExplanationList({
  tokens,
  strings,
  locale,
  fmt,
}: {
  tokens: ExplainToken[];
  strings: Strings;
  locale: string;
  fmt: Format;
}) {
  return (
    <ol className="space-y-1 text-sm">
      {tokens.map((token, index) => (
        <li
          className="flex flex-wrap items-baseline gap-x-3"
          // biome-ignore lint/suspicious/noArrayIndexKey: tokens are positional and may repeat
          key={index}
          style={{ marginInlineStart: `${token.depth * 1.25}rem` }}
        >
          <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-foreground">
            {token.source}
          </code>
          <span className="text-muted-foreground">
            {describeToken(token, strings, locale, fmt)}
          </span>
        </li>
      ))}
    </ol>
  );
}

function groupLabels(match: RegexMatch, strings: Strings): string[] {
  const labels = match.groups.map((value, index) =>
    t(strings.groupNumbered, {
      group: index + 1,
      value: value ?? strings.groupUnmatched,
    })
  );
  for (const [name, value] of match.named) {
    labels.push(
      t(strings.groupNamed, { name, value: value ?? strings.groupUnmatched })
    );
  }
  return labels;
}

export function MatchTable({
  matches,
  strings,
  fmt,
}: {
  matches: RegexMatch[];
  strings: Strings;
  fmt: Format;
}) {
  const [number, matchHead, position, groups] = strings.tableHeads;
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <thead>
          <tr>
            <th className={TH} scope="col">
              {number}
            </th>
            <th className={TH} scope="col">
              {matchHead}
            </th>
            <th className={TH} scope="col">
              {position}
            </th>
            <th className={TH} scope="col">
              {groups}
            </th>
          </tr>
        </thead>
        <tbody>
          {matches.map((match, index) => (
            <tr key={`${match.index}-${match.end}-${index}`}>
              <td className={TD}>{fmt(index + 1)}</td>
              <td className={TD}>
                <code className="whitespace-pre-wrap break-all font-mono">
                  {match.text === "" ? strings.emptyMatch : match.text}
                </code>
              </td>
              <td className={`${TD} whitespace-nowrap font-mono`}>
                {t(strings.position, { start: match.index, end: match.end })}
              </td>
              <td className={`${TD} font-mono text-xs`}>
                {groupLabels(match, strings).map((label) => (
                  <span className="block break-all" key={label}>
                    {label}
                  </span>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
